package com.siriusxm.pia.views.unifiedaggregator

import com.siriusxm.cmc.utils.decodeURIComponent
import com.siriusxm.cmc.utils.encodeURIComponent

/**
 * The query-string half of a URL, as an ordered list of name/value pairs.
 *
 * Exists so a destination can carry query state without every call site hand-assembling
 * `"?a=b&c=d"` and remembering to encode both halves of every pair. Together with [RouteLocation] it
 * is the whole reason `NavKey` returns a structured location rather than a flat `String`: filters are
 * query state, and a `String` has nowhere to put them (see
 * `plans/2026-09-01-UCA-1028-producer-filter-url-state.md`, shortcoming #2).
 *
 * ### Why not `Map<String, String>`
 *
 * Ordered pairs rather than a map so that repeated names survive (`?type=a&type=b`) and so the
 * serialised order is stable — an unstable order would make otherwise-identical URLs compare unequal
 * and churn the browser history. A name present with an empty value (`?search=`) is also distinct
 * from an absent one, which a `Map<String, String>` can express but a `Map<String, String?>` muddles.
 *
 * ### Why not `Route.parameter()`
 *
 * `cmc-core`'s accessor has three behaviours this layer cannot live with, and that module is
 * published to Artifactory and consumed by the EPG app, so it is not ours to fix:
 *
 * - it splits each pair with `split("=")` and keeps only the results of size 2, silently dropping any
 *   pair whose value contains an unencoded `=`;
 * - it maps a value equal to the literal text `null` to Kotlin `null`, so `?search=null` cannot mean
 *   "search for the word null";
 * - it does not decode `+` as space, which both browsers and hand-written links produce.
 *
 * @property pairs the name/value pairs, decoded, in URL order.
 */
data class Query(private val pairs: List<Pair<String, String>>) {

    /** The first value for [name], or null if it is absent. Empty string if present but empty. */
    operator fun get(name: String): String? = pairs.firstOrNull { it.first == name }?.second

    /** Every value for [name], in URL order. Empty when absent. */
    fun all(name: String): List<String> = pairs.filter { it.first == name }.map { it.second }

    /**
     * This query with [name] set to [value], or removed entirely when [value] is null.
     *
     * Replaces the first existing occurrence in place — keeping its position — so that toggling a
     * filter back and forth produces the same URL each time rather than shuffling parameter order.
     * Any further occurrences of the same name are dropped, since a caller setting a single value
     * cannot have meant to leave a second one behind.
     */
    fun with(name: String, value: String?): Query {
        val without = pairs.filter { it.first != name }
        if (value == null) return Query(without)

        val index = pairs.indexOfFirst { it.first == name }
        return if (index < 0) {
            Query(without + (name to value))
        } else {
            Query(without.toMutableList().apply { add(minOf(index, size), name to value) })
        }
    }

    fun isEmpty(): Boolean = pairs.isEmpty()

    fun isNotEmpty(): Boolean = pairs.isNotEmpty()

    /**
     * The serialised query, encoded, **without** a leading `?` — and empty when there are no pairs.
     *
     * One of only two places in the navigation layer that calls `encodeURIComponent`; the other is
     * [RouteLocation.toPath]. Keeping it to two is what makes "did this call site encode?" a question
     * with a single answer.
     */
    fun toQueryString(): String = pairs.joinToString("&") { (name, value) ->
        "${encodeURIComponent(name)}=${encodeURIComponent(value)}"
    }

    companion object {
        val Empty: Query = Query(emptyList())

        /**
         * Parse a raw query string — with or without a leading `?`, possibly empty.
         *
         * Total by construction: there is no such thing as an unparseable query here. A pair is
         * split at its **first** `=` only, so a value containing an unencoded `=` survives intact; a
         * fragment with no `=` becomes a name with an empty value; `+` decodes to a space before
         * percent-decoding, matching what browsers and hand-typed links produce.
         *
         * `decodeURIComponent` *throws* on malformed input such as `%zz`, so each half is decoded
         * defensively and a pair that cannot be decoded is dropped. Dropping is the right answer
         * because every consumer of a filter parameter treats absence as "unfiltered" — a
         * malformed value should show an unfiltered page, not an error.
         */
        fun parse(raw: String): Query {
            val body = raw.removePrefix("?")
            if (body.isEmpty()) return Empty

            return Query(body.split("&").mapNotNull { fragment ->
                if (fragment.isEmpty()) return@mapNotNull null
                val name = decodeOrNull(fragment.substringBefore("=")) ?: return@mapNotNull null
                if (name.isEmpty()) return@mapNotNull null
                val value = decodeOrNull(fragment.substringAfter("=", "")) ?: return@mapNotNull null
                name to value
            })
        }

        private fun decodeOrNull(encoded: String): String? = try {
            decodeURIComponent(encoded.replace("+", " "))
        } catch (_: Throwable) {
            null
        }
    }
}

/**
 * A destination as a list of raw path segments plus a [Query].
 *
 * The structured replacement for the flat path `String` the UCA-1028 pilot used. Two things follow
 * from the shape:
 *
 * - **Segments are raw.** A [NavKey] declares what it means (`"sxm/mddb-bus/mddb-event-bus"`), not
 *   how it is spelled in a URL (`sxm%2Fmddb-bus%2Fmddb-event-bus`). Encoding happens once, in
 *   [toPath], so a destination author cannot forget it.
 * - **Path and query are separate all the way down.** `cmc-core` hands routes the path and the query
 *   separately already, so this matches the seam rather than fighting it, and a caller never has to
 *   find the `?` in a string to know where the segments end.
 *
 * @property segments the path segments, application-relative and unencoded.
 * @property query the query state, if any.
 */
data class RouteLocation(val segments: List<String>, val query: Query = Query.Empty) {

    /**
     * This location as an application-relative path, encoded — e.g.
     * `producers/sxm%2Fmddb-bus%2Fmddb-event-bus/events?range=12h`.
     *
     * An empty [query] emits no `?` at all, which is what keeps links produced before filters existed
     * byte-identical to the ones produced now: an unfiltered view serialises to exactly today's URL
     * rather than to a URL that merely behaves the same.
     */
    fun toPath(): String {
        val path = segments.joinToString("/") { encodeURIComponent(it) }
        return if (query.isEmpty()) path else "$path?${query.toQueryString()}"
    }
}
